export enum SortOrder {
  ASCENDING,
  DESCENDING
}

export enum DataType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
}

type comparatorFn = (valA: any, valB: any) => number

const COMPARATOR_MAP: { [k in DataType]: comparatorFn } = {
  [DataType.STRING]: (valA, valB) => COMPARE_STRING(valA, valB),
  [DataType.NUMBER]: (valA, valB) => COMPARE_NUMBER(valA, valB),
};

/**
 * Function to sort an array using a specific attribute/type, you can also leave the @param {field} empty,
 * for sorting the values itself, and not a property of the value
 *
 * @param {T[]} arr The array to be sorted
 * @param {SortOrder} order The order to sort the array
 * @param {DataType} dataType The type of the values being sorted
 * @param {string} attribute OPTIONAL, the attribute to be sorted (in case the array is not of primitives),
 * you can leave this field blank to sort from the root
 *
 * @return Sorted array
 */
export function SORT_ARRAY_BY_FIELD<T>(arr: T[], order: SortOrder, dataType: DataType, attribute?: keyof T): T[] {
  const newArr = [...arr];
  const isDescending = order === SortOrder.DESCENDING;

  newArr.sort((valA, valB) => {
    const valueA = attribute ? valA[attribute] : valA;
    const valueB = attribute ? valB[attribute] : valB;

    const result = COMPARATOR_MAP[dataType](valueA, valueB);

    return isDescending ? -result : result;
  });

  return newArr;
}

/**
 * Compare two string values
 *
 * @param {string} valA Left side argument
 * @param {string} valB Right side argument
 *
 * @return {number} Value representing the correct order for the parameters
 */
export function COMPARE_STRING(valA: string, valB: string): number {
  // Prevent code stopping if values are not string
  const valueA = `${valA}`;
  const valueB = `${valB}`;

  return valueA.localeCompare(valueB);
}

/**
 * Compare two number values
 *
 * @param {number} valA Left side argument
 * @param {number} valB Right side argument
 *
 * @return {number} Value representing the correct order for the parameters
 */
export function COMPARE_NUMBER(valA: number, valB: number): number {
  // Prevent code stopping if values are nullable
  const valueA = valA ?? 0;
  const valueB = valB ?? 0;

  return valueA - valueB;
}

/**
 * This function returns unique values for a primitive array, notice that this won't work with an array of {object|array}
 * @param {T[]} arr Array to be modified
 *
 * @return {T[]} Array with unique values
 */
export function UNIQUE<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}
